"use client";

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BuyMinutesModal } from "@features/subscription/BuyMinutesModal";
import { EmailRequestModal } from "@features/subscription/EmailRequestModal";
import { SubscriptionQuoteModal } from "@features/subscription/SubscriptionQuoteModal";
import { type CheckoutPlan, useSubscriptionCheckout } from "@features/subscription/useSubscriptionCheckout";
import { type BillingPeriod } from "@shared/api/subscription.api";
import { PRICING_FACTS } from "@shared/config/site-facts";
import { useAuth } from "@shared/contexts/auth-context";
import { useToast } from "@shared/helper_lib/hooks/use-toast";
import { useBalance } from "@shared/hooks/useBalance";
import { Badge, Button, Card } from "@shared/ui/components";
import { Check, X, Zap } from "lucide-react";

interface PlanFeature {
  text: string;
  available: boolean;
}

const formatRub = (value: number): string => `${Math.round(value).toLocaleString("ru-RU")} ₽`;

const formatRubPerMinute = (value: number): string =>
  `${value.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;

const formatRubTrim = (value: number): string => `${value.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`;

interface YearExtra {
  crossedPrice: string;
}

interface PlanView {
  name: string;
  nameEn: "trial" | "basic" | "pro";
  headerNote: string | null;
  headerNoteAccent: boolean;
  description: string;
  minutes: string;
  minutesSuffix: string;
  barWidth: string;
  price: string;
  period: string;
  pricePerMinute: string;
  savings: string | null;
  yearExtra: YearExtra | null;
  features: PlanFeature[];
  cta: string;
  footnote: string;
}

function buildPlans(period: BillingPeriod): PlanView[] {
  const { trial, basic, pro } = PRICING_FACTS;

  return [
    {
      name: "Пробный",
      nameEn: "trial",
      headerNote: "7 дней",
      headerNoteAccent: true,
      description: "Разовый вход, чтобы оценить точность на своём материале.",
      minutes: trial.minutes.toLocaleString("ru-RU"),
      minutesSuffix: "минут · ≈ 2,5 часа записи",
      barWidth: "7.5%",
      price: formatRub(trial.price),
      period: "единоразово",
      pricePerMinute: formatRubPerMinute(trial.price / trial.minutes),
      savings: null,
      yearExtra: null,
      features: [
        { text: "Высокий приоритет обработки", available: true },
        { text: "5 AI-конспектов", available: true },
        { text: "Докупка минут недоступна", available: false }
      ],
      cta: `Взять ${trial.minutes.toLocaleString("ru-RU")} минут за ${formatRub(trial.price)}`,
      footnote: `Дальше — Базовый за ${formatRub(basic.price)}/мес, отмена в один клик`
    },
    {
      name: "Базовый",
      nameEn: "basic",
      headerNote: "подписка",
      headerNoteAccent: false,
      description: "Три-четыре часа расшифровки в неделю без мыслей о балансе.",
      minutes: basic.minutes.toLocaleString("ru-RU"),
      minutesSuffix: "минут в месяц · ≈ 7,5 часа",
      barWidth: "22.5%",
      price: period === "year" ? formatRub(basic.yearlyPrice / 12) : formatRub(basic.price),
      period: period === "year" ? "/ в месяц" : "в месяц",
      pricePerMinute:
        period === "year"
          ? formatRubPerMinute(basic.yearlyPrice / 12 / basic.minutes)
          : formatRubPerMinute(basic.price / basic.minutes),
      savings: null,
      yearExtra:
        period === "year"
          ? {
              crossedPrice: formatRub(basic.price)
            }
          : null,
      features: [
        { text: "Высокий приоритет обработки", available: true },
        { text: "25 AI-конспектов в месяц", available: true },
        { text: `Докупка минут по ${formatRubTrim(basic.extraMinutePrice)} в любой момент`, available: true }
      ],
      cta: "Перейти на Базовый",
      footnote: "Отмена в один клик, минуты остаются до конца периода"
    },
    {
      name: "Профи",
      nameEn: "pro",
      headerNote: null,
      headerNoteAccent: false,
      description: "Ежедневный поток записей: команда, продакшен, исследования.",
      minutes: pro.minutes.toLocaleString("ru-RU"),
      minutesSuffix: "минут в месяц · ≈ 33 часа",
      barWidth: "100%",
      price: period === "year" ? formatRub(pro.yearlyPrice / 12) : formatRub(pro.price),
      period: period === "year" ? "/ в месяц" : "в месяц",
      pricePerMinute:
        period === "year"
          ? formatRubPerMinute(pro.yearlyPrice / 12 / pro.minutes)
          : formatRubPerMinute(pro.price / pro.minutes),
      savings: "минута дешевле в 1,8 раза",
      yearExtra:
        period === "year"
          ? {
              crossedPrice: formatRub(pro.price)
            }
          : null,
      features: [
        { text: "Наивысший приоритет — старт без очереди", available: true },
        { text: "50 AI-конспектов в месяц", available: true },
        { text: `Докупка минут по ${formatRubTrim(pro.extraMinutePrice)} и поддержка 24/7`, available: true }
      ],
      cta: "Перейти на Профи",
      footnote: `Тот же объём минутами вышел бы в ${formatRub(pro.minutes * basic.extraMinutePrice)}`
    }
  ];
}

// Маппинг русских названий тарифов на английские
const planNameMapping: Record<string, string> = {
  Бесплатный: "free",
  Пробный: "trial",
  Базовый: "basic",
  Профи: "pro"
};

interface PricingCardsProps {
  currentPlan?: string; // Добавляем пропс currentPlan
  recommendedPlan?: "trial" | "basic" | "pro" | null;
}

export function PricingCards({ currentPlan, recommendedPlan }: PricingCardsProps): JSX.Element {
  const { isAuthenticated, openAuthModal } = useAuth();
  const { balance, refetch } = useBalance();
  const { toast } = useToast();
  const { startCheckout, loadingPlan, emailModalProps, quoteModalProps } = useSubscriptionCheckout({
    onError: message => toast({ title: "Ошибка", description: message, variant: "destructive" })
  });
  const [buyMinutesOpen, setBuyMinutesOpen] = useState(false);
  const [selectedPlanForMinutes, setSelectedPlanForMinutes] = useState<string | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();

  const period: BillingPeriod = searchParams.get("period") === "year" ? "year" : "month";

  const handlePeriodChange = (value: BillingPeriod): void => {
    const next = new URLSearchParams(searchParams);
    if (value === "year") {
      next.set("period", "year");
    } else {
      next.delete("period");
    }
    setSearchParams(next, { replace: true });
  };

  // Обработчик для кнопки докупки минут
  const handleBuyMinutes = (planName: string): void => {
    // Если пользователь не авторизован, показываем окно авторизации
    if (!isAuthenticated) {
      openAuthModal("signup");
      return;
    }
    setSelectedPlanForMinutes(planName);
    setBuyMinutesOpen(true);
  };

  // Преобразуем currentPlan из русского в английский, если он есть
  const mappedCurrentPlan = currentPlan ? planNameMapping[currentPlan] : undefined;

  const plans = buildPlans(period);

  return (
    <div>
      <div className="mb-6 flex justify-center">
        <div className="inline-flex rounded-md border border-border bg-muted p-1">
          <button
            type="button"
            aria-pressed={period === "month"}
            onClick={() => handlePeriodChange("month")}
            className={`rounded-sm px-5 py-1.5 text-sm font-medium transition-colors ${
              period === "month" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
            }`}
          >
            Месяц
          </button>
          <button
            type="button"
            aria-pressed={period === "year"}
            onClick={() => handlePeriodChange("year")}
            className={`flex items-center gap-2 rounded-sm px-5 py-1.5 text-sm font-medium transition-colors ${
              period === "year" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
            }`}
          >
            Год
            <Badge variant="secondary" className="text-[11px]">
              −{PRICING_FACTS.yearlyDiscountPercent}%
            </Badge>
          </button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3 items-stretch">
        {plans.map(plan => {
          const isCurrentPlan =
            isAuthenticated &&
            mappedCurrentPlan === plan.nameEn &&
            (plan.nameEn === "trial" || (balance?.billingPeriod ?? "month") === period); // Используем mappedCurrentPlan
          const isRecommended =
            recommendedPlan !== undefined && recommendedPlan !== null
              ? recommendedPlan === plan.nameEn
              : plan.nameEn === "basic";
          const isDimmedTrial = period === "year" && plan.nameEn === "trial";

          return (
            <Card
              key={plan.name}
              className={`flex flex-col gap-0 overflow-hidden py-0 transition-opacity duration-500 ease-out ${
                isRecommended ? "border-primary border-[1.5px] shadow-lg" : ""
              } ${isDimmedTrial ? "opacity-60" : ""}`}
            >
              {isRecommended && (
                <div className="flex h-10 items-center justify-center gap-2 bg-primary text-[13px] font-semibold text-primary-foreground">
                  <Zap className="h-3.5 w-3.5" />
                  Оптимально для большинства
                </div>
              )}

              <div className={`flex flex-col gap-1.5 px-[26px] ${isRecommended ? "pt-6" : "pt-[26px]"}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-[22px] font-bold tracking-[-0.01em] text-foreground">{plan.name}</h3>
                  {plan.savings ? (
                    <Badge variant="secondary" className="shrink-0 text-[11px]">
                      {plan.savings}
                    </Badge>
                  ) : (
                    plan.headerNote && (
                      <span
                        className={`shrink-0 text-xs font-semibold ${
                          plan.headerNoteAccent ? "text-green-600 dark:text-green-500" : "text-muted-foreground"
                        }`}
                      >
                        {plan.headerNote}
                      </span>
                    )
                  )}
                </div>
                <p className="min-h-[42px] text-sm leading-[1.5] text-muted-foreground">{plan.description}</p>
              </div>

              {/* Объём минут: цифра и полоса относительного объёма */}
              <div className="flex flex-col gap-3 px-[26px] pb-[22px] pt-5">
                <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                  <span className="text-[34px] font-bold leading-none tracking-[-0.02em] text-foreground">
                    {plan.minutes}
                  </span>
                  <span className="text-sm text-muted-foreground">{plan.minutesSuffix}</span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full min-w-[28px] rounded-full ${plan.nameEn === "trial" ? "bg-green-600" : "bg-primary"}`}
                    style={{ width: plan.barWidth }}
                  />
                </div>
              </div>

              {/* Цена периода и цена минуты */}
              <div className="mx-[26px] flex flex-wrap items-center justify-between gap-3 border-y border-border py-[18px]">
                <div className="flex flex-col gap-0.5">
                  <div className="flex flex-nowrap items-baseline gap-x-2 whitespace-nowrap">
                    <span
                      key={`price-${period}`}
                      className="text-[28px] font-bold leading-none tracking-[-0.02em] text-foreground animate-in fade-in duration-500 ease-out"
                    >
                      {plan.price}
                    </span>
                    <span
                      aria-hidden={!plan.yearExtra}
                      className={`text-sm leading-none text-muted-foreground/70 line-through transition-opacity duration-500 ease-out ${
                        plan.yearExtra ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {plan.yearExtra?.crossedPrice ?? ""}
                    </span>
                  </div>
                  <span className="text-[13px] text-muted-foreground">{plan.period}</span>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span
                    key={`ppm-${period}`}
                    className="text-lg font-semibold text-foreground animate-in fade-in duration-500 ease-out"
                  >
                    {plan.pricePerMinute}
                  </span>
                  <span className="text-xs text-muted-foreground/70">за минуту</span>
                </div>
              </div>

              {/* Отличия тарифа — функции одинаковые на всех планах */}
              <div className="flex flex-grow flex-col gap-[11px] px-[26px] pt-5">
                {plan.features.map(feature => (
                  <div key={feature.text} className="flex items-start gap-2.5">
                    {feature.available ? (
                      <Check className="mt-0.5 h-[17px] w-[17px] shrink-0 text-foreground" />
                    ) : (
                      <X className="mt-0.5 h-[17px] w-[17px] shrink-0 text-muted-foreground/70" />
                    )}
                    <span
                      className={`text-sm leading-snug ${feature.available ? "text-foreground" : "text-muted-foreground/70"}`}
                    >
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-[9px] px-[26px] pb-[26px] pt-6">
                <Button
                  className={`h-[46px] w-full text-[15px] font-semibold ${isCurrentPlan ? "cursor-not-allowed ring-2 ring-primary" : ""}`}
                  variant={isRecommended ? "default" : "outline"}
                  onClick={() => startCheckout(plan.nameEn as CheckoutPlan, plan.nameEn === "trial" ? {} : { period })}
                  disabled={isCurrentPlan || isDimmedTrial || loadingPlan === plan.nameEn}
                >
                  {isCurrentPlan ? "Текущий тариф" : loadingPlan === plan.nameEn ? "Обработка..." : plan.cta}
                </Button>

                {/* Кнопка докупки минут */}
                {/* Для авторизованных - показываем только на текущем тарифе, кроме пробного */}
                {isCurrentPlan && plan.nameEn !== "trial" && (
                  <Button
                    className="h-[46px] w-full bg-white text-[15px] font-semibold dark:bg-background"
                    variant="outline"
                    onClick={() => handleBuyMinutes(plan.nameEn)}
                  >
                    Докупить минуты
                  </Button>
                )}

                <p className="text-center text-xs leading-[1.45] text-muted-foreground/70">{plan.footnote}</p>
              </div>
            </Card>
          );
        })}
        {balance && (
          <BuyMinutesModal
            open={buyMinutesOpen}
            onClose={() => {
              setBuyMinutesOpen(false);
              setSelectedPlanForMinutes(undefined);
            }}
            balance={{
              planName: balance.planName,
              extraMinutePrice: balance.extraMinutePrice
            }}
            onSuccess={refetch}
            planName={selectedPlanForMinutes}
          />
        )}

        <EmailRequestModal {...emailModalProps} />
        <SubscriptionQuoteModal {...quoteModalProps} />
      </div>
    </div>
  );
}
